import { Component, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { useTheme } from '@emotion/react'
import useMediaQuery from '@mui/material/useMediaQuery'
import posed, { PoseGroup } from 'react-pose'

import { RetirementIncomeDonut, RetirementIncomeValues } from '../../../components'
import { incomeGoalLayout } from '../../../components/RetirementIncomeDonut'
import { MOBILE_VIEWPORT } from '../../../guided-toolbox/utils/breakpoints'
import CouldYouLiveHelp from '../../goals/IncomeReplacement/CouldYouLiveHelp'
import IncomeReplacementTellMeMore, {
  SEEM_LOW_NOTE,
} from '../../goals/IncomeReplacement/IncomeReplacementTellMeMore'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../models/Person'
import {
  includesRetiredSpouse,
  incomeGoalToneLabel,
  projectedIncomeValues,
  formatPercentLabel,
} from '../../../utils'
import { Page } from '../elements'
import IncomeGoalDecision from './IncomeGoalDecision'
import {
  Content,
  DONUT_PADDING,
  Heading,
  HeadingEmphasis,
  Intro,
  GoalBody,
  ChartCell,
  ToneLabel,
  ValuesCell,
  CouldYouLive,
  TellMeMoreSection,
} from './IncomeGoalStyle'

const RouteContainer = posed.div({
  enter: { opacity: 1, delay: 150, beforeChildren: true },
  exit: { opacity: 0 },
})

const INTRO_COPY =
  'Your biggest financial commitment is funding your retirement. Whatever retirement means to you, you want to have enough to meet your needs and then provide for other things you want from discretionary spending to big ticket items, such as a vacation or a car.'

const IncomeGoalContent = observer(({ onboarding }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const [showValueAtRetirement, setShowValueAtRetirement] = useState(false)
  const [showTellMeMore, setShowTellMeMore] = useState(false)

  const { baseCase, person, spouse } = onboarding
  const goalPercent = person?.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT
  const values = projectedIncomeValues({ retirementIncomeGoalPct: goalPercent, baseCase })
  // The goal covers the person's income alone once a spouse has stopped
  // earning, so point at the explanation rather than leave the drop unexplained.
  const spouseRetired = includesRetiredSpouse({ person, spouse })

  return (
    <Content>
      <Page.AnimateItem>
        <Heading>
          Do you expect your <HeadingEmphasis>costs in retirement</HeadingEmphasis> to be lower,
          higher or the same as today?
        </Heading>
      </Page.AnimateItem>

      <Page.AnimateItem>
        <Intro>{INTRO_COPY}</Intro>
      </Page.AnimateItem>

      <Page.AnimateItem>
        <GoalBody>
          <ChartCell>
            <ToneLabel>{incomeGoalToneLabel(goalPercent)}</ToneLabel>
            <RetirementIncomeDonut
              centerValue={`${formatPercentLabel(goalPercent)}%`}
              monthlyLabel='of current income'
              replacementPercent={goalPercent}
              /* The plan arc already shows the goal, so disable the separate goal band. */
              goalPercent={0}
              layout={{ ...incomeGoalLayout(isMobile), padding: DONUT_PADDING }}
              colors={{ digits: theme.primaryColor, monthly: theme.primaryColor }}
            />
          </ChartCell>

          <ValuesCell>
            <CouldYouLive>
              Could you live on this amount?
              <CouldYouLiveHelp />
            </CouldYouLive>
            <RetirementIncomeValues
              values={values}
              compact={isMobile}
              emphasizeToday
              /* Mobile doesn't have room for the help icon beside the label. */
              showAtRetirementHelp={!isMobile}
              expanded={showValueAtRetirement}
              onToggle={() => setShowValueAtRetirement(shown => !shown)}
              todaysNote={spouseRetired ? SEEM_LOW_NOTE : null}
            />
          </ValuesCell>
        </GoalBody>
      </Page.AnimateItem>

      <Page.AnimateItem>
        <TellMeMoreSection>
          <IncomeReplacementTellMeMore
            percent={goalPercent}
            source={onboarding}
            expanded={showTellMeMore}
            onToggle={() => setShowTellMeMore(shown => !shown)}
          />
        </TellMeMoreSection>
      </Page.AnimateItem>

      <IncomeGoalDecision />
    </Content>
  )
})

class IncomeGoal extends Component {
  state = { status: 'loading' }

  async fetchData() {
    try {
      await this.props.onboarding.getBaseCase()
      this.setState({ status: 'done' })
    } catch (err) {
      console.error(err)
      this.setState({ status: 'error' })
    }
  }

  componentDidMount() {
    this.fetchData()
  }

  render() {
    return (
      <Page>
        <PoseGroup animateOnMount>
          <RouteContainer key='status'>
            {this.state.status === 'loading' && (
              <Page.AnimateItem poseKey='loading_status'>
                <Page.Loading />
              </Page.AnimateItem>
            )}

            {this.state.status === 'error' && (
              <Page.AnimateItem poseKey='error_status'>
                <Page.ErrorMessage>
                  Oops! Something went wrong, please try again later
                </Page.ErrorMessage>
              </Page.AnimateItem>
            )}

            {this.state.status === 'done' && (
              <Page.AnimateGroup poseKey='done_status'>
                <IncomeGoalContent onboarding={this.props.onboarding} />
              </Page.AnimateGroup>
            )}
          </RouteContainer>
        </PoseGroup>
      </Page>
    )
  }
}

export default inject('onboarding')(observer(IncomeGoal))
